import { z } from "zod";

/**
 * User-search wire contract — single source of truth for web + mobile.
 *
 * Closes the documented drift where `hooks/use-paginated-user-search.ts`
 * (web) and `apps/mobile/types/search.ts` (mobile) each hand-declared the same
 * shape (see WEB_MOBILE_CONTRACT.md / SEARCH_AUDIT.md §14.3). Served by
 * `GET /api/search/users` (legacy load-all path and the `search_users` RPC
 * path both return this shape).
 *
 * Schema-first: types derived via `z.infer` — see pin.ts header on why
 * bidirectional `pinned()` does not apply.
 */
// eslint-disable-next-line no-restricted-syntax -- schema-first declaration: UserSearchResult is z.infer<typeof UserSearchResultSchema>.
export const UserSearchResultSchema = z.object({
  id: z.string(),
  username: z.string(),
  avatar_url: z.string().nullable(),
  blurDataURL: z.string().nullable().optional(),
  followers_count: z.number(),
  is_following: z.boolean(),
  // Always populated by the route (sales_count enrichment); required on the wire.
  sales_count: z.number(),
  // Additive seller stats (2026-07 cross-platform contract, lib/profile-stats.ts).
  // Always populated by the route today, but `.optional()` so older cached
  // payloads (mobile) still parse. `rating` is the seller-review average,
  // 1 decimal, null when the profile has no published reviews.
  listings_count: z.number().optional(),
  rating: z.number().nullable().optional(),
  rating_count: z.number().optional(),
});
export type UserSearchResult = z.infer<typeof UserSearchResultSchema>;

// eslint-disable-next-line no-restricted-syntax -- schema-first declaration: UserSearchResponse is z.infer<typeof UserSearchResponseSchema>.
export const UserSearchResponseSchema = z.object({
  items: z.array(UserSearchResultSchema),
  hasNextPage: z.boolean(),
  page: z.number(),
  totalCount: z.number(),
});
export type UserSearchResponse = z.infer<typeof UserSearchResponseSchema>;

/** Request params for `GET /api/search/users` (for the future typed client). */
// eslint-disable-next-line no-restricted-syntax -- schema-first declaration: SearchUsersParams is z.infer<typeof SearchUsersParamsSchema>.
export const SearchUsersParamsSchema = z.object({
  q: z.string().min(1),
  page: z.number().int().positive().default(1),
  limit: z.number().int().positive().max(50).default(20),
});
export type SearchUsersParams = z.infer<typeof SearchUsersParamsSchema>;
